import { UserProfileType } from "@/domain/account.interface";
import { UserGame } from "@/domain/account/profile.interface";
import { Game } from "@/domain/general.interface";
import CardBase from "@apg.gg/core/lib/CardBase";
import Tag from "@apg.gg/core/lib/Tag";
import Tooltip from "@apg.gg/core/lib/Tooltip";
import BetaColorIcon from "@apg.gg/icons/lib/BetaColorIcon";
import VerifiedColorIcon from "@apg.gg/icons/lib/VerifiedColorIcon";
import { id } from "date-fns/locale";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { FC, useState } from "react";
import { useRouter } from 'next-nprogress-bar';
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import useAuthStore from "@/store/auth.store";
import { ProfileEndpoints } from "@/graph/profile.endpoints";
import Button from "@apg.gg/core/lib/Button";
import classNames from "classnames";
import { toast } from "react-toastify";
import Modal from "@apg.gg/core/lib/Modal";

interface CardProfileGameProps {
  user: UserGame;
  game: Game;
}

const CardProfileGame: FC<CardProfileGameProps> = ({ user, game }) => {
  const t = useTranslations('Profile');
  const { name, username, bannerImage, profileImage, profileTypes, isVerified, isBetaUser, userFollower } = user.user || {};
  const { locale } = useParams();
  const { userId, authToken, userData } = useAuthStore();

  const [isHovered, setIsHovered] = useState(false);
  const [isFollowed, setIsFollowed] = useState(userFollower?.total && userFollower?.total.count > 0);
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const router = useRouter();
  const hasTags = !!profileTypes && profileTypes.length > 0;

  const userTags = profileTypes?.map((profileType: UserProfileType) => {
    return {
      id: String(profileType.profileType?.id),
      title: profileType.profileType?.name || '',
      type: profileType.profileType?.color,
      icon: profileType.profileType?.icon,
      link: `/community?profileType=${profileType.profileType?.slug}`,
    }
  }) || [];

  const onFollow = async () => {
    try {
      await ProfileEndpoints.follow({
        followingId: user.id,
        token: authToken
      });

      toast.success(`${t('followed-successfully')} ${name}`);
      setIsFollowed(true);
    } catch (error) {
      toast.error(`${t('followed-failed')} ${name}`);
    }
  }

  const onUnfollow = async () => {
    try {
      await ProfileEndpoints.unfollow({
        userId: userId as number,
        followingId: user.id,
        token: authToken
      });
      toast.success(`${t('unfollowed-successfully')} ${name}`);
      setIsFollowed(false);
    } catch (error) {
      toast.error(`${t('unfollowed-failed')} ${name}`);
    }
  }

  return (
    <>
      <motion.div
        key={`game-${game.name}-${user.id}`}
        layout
        initial={{ transform: "scale(0)" }}
        animate={{ transform: "scale(1)" }}
        exit={{ transform: "scale(0)" }}
        role="button"
        onClick={() => router.push(`/${locale}/${username}`)}
      >
        <CardBase
          className={'flex flex-col w-full max-w-[420px] max-h-[370px]'}
          classNameWrapper="flex flex-col h-[27rem] gap-2 overflow-visible"
        >
          <div className="h-20 relative rounded-t-xl overflow-hidden mx-1.5 mt-2.5">
            <Link href={`/${locale}/${username}`}>
              <Image src={bannerImage || "https://statics.apg.gg/default/post.png"} alt={user.playerTag} className="w-full h-full object-cover" width={360} height={80} />
            </Link>
          </div>

          <div className="card-profile-header flex w-full px-3.5 justify-start">
            <Link href={`/${locale}/${username}`} className="flex items-center w-[90px] h-auto min-w-[3rem] -mt-[15%] relative z-40">
              <Image className="w-[90px] h-[90px] border-2 border-black-800 rounded-full" alt={user.playerTag} src={profileImage || `${process.env.NEXT_PUBLIC_DEFAULT_PROFILE_PIC}`} draggable="false" width={90} height={90} />
            </Link>

            <div className="flex flex-col gap-2 ml-auto">
              {username !== userData?.username ? (
                <div
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  className="ml-auto"
                >
                  <Button
                    fontSize="xs"
                    onClick={async () => {
                      if (isFollowed) {
                        setShowUnfollowModal(true);
                      } else {
                        if (authToken) {
                          onFollow?.();
                        } else {
                          router.push(`/${locale}/auth/login`);
                        }
                      }
                    }}
                    className={classNames(
                      {
                        "hover:bg-red w-28": isFollowed
                      }
                    )}
                  >
                    {isFollowed ? (isHovered ? t('button-unfollow') : t('button-following')) : t('button-follow')}
                  </Button>
                </div>
              ) : null }
            </div>
          </div>

          <div className="flex flex-col w-full px-3.5">
            <div className="flex items-center gap-2">
              <Link href={`/${locale}/${username}`} className="cursor-pointer">
                <h4 className="text-white text-title-md font-bold !cursor-pointer">{username}</h4>
              </Link>
              {isVerified ? (
                <Tooltip placement="top" text={t('this-account-is-verified')}>
                  <VerifiedColorIcon className="text-lg flex cursor-pointer" />
                </Tooltip>
              ) : null}
              {isBetaUser ? (
                <Tooltip placement="top" text={t('this-account-is-beta-tester')}>
                  <BetaColorIcon className="text-lg flex cursor-pointer" />
                </Tooltip>
              ) : null}
            </div>
            {username && (
              <div className="flex gap-[10px] items-center text-black-400 text-sm leading-3">
                <Link href={`/${locale}/${username}`} className="cursor-pointer">
                  @{username}
                </Link>
              </div>
            )}
          </div>

          <div className="flex flex-col w-full px-3.5 mt-4">
            <div className="flex gap-2 items-center">
              <strong className="text-black-400 text-sm leading-3">{t('playertag')}:</strong>
              <span className="text-body-md">{user.playerTag}</span>
            </div>
            <div className="flex gap-2 items-center">
              <strong className="text-black-400 text-sm leading-3">{t('clasification')}:</strong>
              <span className="text-body-md">{user.clasification.name}</span>
            </div>
          </div>

          {hasTags && (
            <div className="flex flex-wrap gap-2 px-3.5 pb-4 mt-auto">
              {userTags?.map((tag: any) => (
                <button
                  key={`${id}-${tag.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push(`/${locale}/${tag.link}`)
                  }}
                >
                  <Tag
                    icon={tag.icon}
                    className="max-w-none"
                    type={tag.type}
                  >
                    {tag.title}
                  </Tag>
                </button>
              ))}
            </div>
          )}
        </CardBase>
      </motion.div>

       {/* Unfollow Modal */}
       {showUnfollowModal ? (
        <Modal
          prefixCls="apg-modal"
          isOpen={showUnfollowModal}
          onClose={() => setShowUnfollowModal(false)}
          title={t('unfollow-modal-title', {
            username: username
          })}
          content={t('unfollow-modal-description', {
            username: username
          })}
          confirmText={t('button-unfollow')}
          cancelText={t('button-cancel')}
          onConfirm={onUnfollow}
          centered
          classNames={{
            wrapper: "max-w-xs",
            header: "text-title-lg",
            content: "text-body-lg text-white-200"
          }}
        />
      ) : null}
    </>
  )
}

export default CardProfileGame